import { currentLandingFaq, rentalLanding } from './landingRental';
import { findGuide } from '../guides/guidesContent';

export const LANDING_ORIGIN = 'https://mediadorimobiliariodigital.com';
const landingTitle = () => (rentalLanding() ? 'Procurar casa em Portugal: cria o teu plano | miD' : 'Comprar casa em Portugal: cria o teu plano | miD');
// Zonamento switch, read from process.env like lib/brand.js: CRA inlines it in the bundle
// and prerenderLanding.ts runs this module under Node (mid-v6k8.8).
const brandDir = () => (['1', 'true', 'yes', 'on'].includes((process.env.REACT_APP_NEW_BRAND || '').trim().toLowerCase()) ? '/brand/z' : '/brand');
const landingDescription = () => (rentalLanding()
  ? 'Procurar casa em Portugal? Cria o teu Buyer DNA ou Renter DNA, compara anúncios e estima quanto vais pagar. Gratuito, e começas sem conta.'
  : 'Cria o teu Buyer DNA, compara anúncios de casas em Portugal e estima custos de compra com o miD. Gratuito para compradores. Começa sem criar conta.');

export function getLandingMetadata(pathname: string) {
  const about = pathname === '/sobre';
  const publicPages: Record<string, { title: string; description: string }> = {
    '/precos-agentes': { title: 'Planos para agentes imobiliários: Observador e Premium | miD', description: 'Consulta os planos para agentes imobiliários no miD. Explora a procura nas tuas zonas e compara as ferramentas dos planos Observador e Premium.' },
    '/privacy': { title: 'Política de Privacidade | miD', description: 'Consulta como o miD recolhe, utiliza e protege os teus dados pessoais e conhece os teus direitos.' },
    '/cookies': { title: 'Política de Cookies | miD', description: 'Consulta os cookies usados pelo miD e como gerir as tuas preferências de privacidade.' },
  };
  const guide = findGuide(pathname);
  const title = guide?.title || publicPages[pathname]?.title || (about ? 'Como comprar casa com Buyer DNA e Matchmaker | miD' : landingTitle());
  const description = guide?.description || publicPages[pathname]?.description || (about ? 'Descobre como o miD te ajuda a comprar casa em Portugal: Buyer DNA, Matchmaker e Buyer Readiness Score. Ferramentas gratuitas e respostas às tuas dúvidas.' : landingDescription());
  const url = `${LANDING_ORIGIN}${pathname}`;
  const brand = `${LANDING_ORIGIN}${brandDir()}`;
  const image = `${brand}/landing-share.png`;
  return {
    title,
    tags: [
      ['meta', 'name', 'description', 'content', description],
      ['meta', 'name', 'robots', 'content', 'index, follow, max-image-preview:large'],
      ['link', 'rel', 'canonical', 'href', url],
      ['meta', 'property', 'og:title', 'content', title],
      ['meta', 'property', 'og:description', 'content', description],
      ['meta', 'property', 'og:url', 'content', url],
      ['meta', 'property', 'og:type', 'content', 'website'],
      ['meta', 'property', 'og:locale', 'content', 'pt_PT'],
      ['meta', 'property', 'og:site_name', 'content', 'miD'],
      ['meta', 'property', 'og:image', 'content', image],
      ['meta', 'property', 'og:image:width', 'content', '1200'],
      ['meta', 'property', 'og:image:height', 'content', '630'],
      ['meta', 'property', 'og:image:alt', 'content', `miD — ${rentalLanding() ? 'Procurar' : 'Comprar'} casa em Portugal começa por um plano`],
      ['meta', 'name', 'twitter:card', 'content', 'summary_large_image'],
      ['meta', 'name', 'twitter:title', 'content', title],
      ['meta', 'name', 'twitter:description', 'content', description],
      ['meta', 'name', 'twitter:image', 'content', image],
    ],
    structuredData: {
      '@context': 'https://schema.org',
      '@graph': [
        { '@type': 'Organization', name: 'miD', alternateName: 'Mediador Imobiliário Digital', url: `${LANDING_ORIGIN}/`, logo: `${brand}/mid-logo.png`, email: 'geral@mediadorimobiliariodigital.com' },
        { '@type': 'WebSite', name: 'miD', url: `${LANDING_ORIGIN}/`, inLanguage: 'pt-PT' },
        { '@type': 'WebPage', name: title, description, url, inLanguage: 'pt-PT' },
        ...(guide ? [{
          '@type': 'Article',
          headline: guide.h1,
          description,
          url,
          inLanguage: 'pt-PT',
          dateModified: guide.reviewedOn,
          author: { '@type': 'Organization', name: 'miD', url: `${LANDING_ORIGIN}/` },
          publisher: { '@type': 'Organization', name: 'miD', url: `${LANDING_ORIGIN}/` },
        }, {
          '@type': 'FAQPage', url,
          mainEntity: guide.faq.map(({ question, answer }) => ({
            '@type': 'Question', name: question, acceptedAnswer: { '@type': 'Answer', text: answer },
          })),
        }] : []),
        ...(about || pathname === '/' ? [{
          '@type': 'FAQPage', url,
          mainEntity: currentLandingFaq().map(({ question, answer }: { question: string; answer: string }) => ({
            '@type': 'Question', name: question, acceptedAnswer: { '@type': 'Answer', text: answer },
          })),
        }] : []),
      ],
    },
  };
}

// Update existing tags so navigating from prerendered HTML never duplicates metadata.
export function applyLandingMetadata(pathname: string, includeFaq = true) {
  const previousTitle = document.title;
  const previousLanguage = document.documentElement.lang;
  const metadata = getLandingMetadata(pathname);
  document.title = metadata.title;
  document.documentElement.lang = 'pt-PT';
  const restoreTags = metadata.tags.map(([tag, attribute, value, contentAttribute, content]) => {
    const existing = document.head.querySelector(`${tag}[${attribute}="${value}"]`);
    const element = existing || document.createElement(tag);
    const previousContent = element.getAttribute(contentAttribute);
    element.setAttribute('data-mid-seo', '');
    element.setAttribute(attribute, value);
    element.setAttribute(contentAttribute, content);
    if (!existing) document.head.appendChild(element);
    return () => {
      if (!existing) element.remove();
      else if (previousContent === null) element.removeAttribute(contentAttribute);
      else element.setAttribute(contentAttribute, previousContent);
    };
  });
  const previousSchema = document.head.querySelector('script[data-mid-seo]');
  const schema = previousSchema || document.createElement('script');
  const previousSchemaText = schema.textContent;
  schema.setAttribute('type', 'application/ld+json');
  schema.setAttribute('data-mid-seo', '');
  schema.textContent = JSON.stringify({ ...metadata.structuredData, '@graph': metadata.structuredData['@graph'].filter((item) => includeFaq || item['@type'] !== 'FAQPage') });
  if (!previousSchema) document.head.appendChild(schema);
  return () => {
    document.title = previousTitle;
    document.documentElement.lang = previousLanguage;
    restoreTags.forEach((restore) => restore());
    if (previousSchema) schema.textContent = previousSchemaText;
    else schema.remove();
  };
}


export function clearLandingMetadata() {
  document.querySelectorAll('[data-mid-seo]').forEach((element) => element.remove());
  document.title = 'miD — Mediador Imobiliário Digital';
  const robots = document.head.querySelector('meta[name="robots"]') || document.createElement('meta');
  robots.setAttribute('name', 'robots');
  robots.setAttribute('content', 'noindex, follow');
  if (!robots.parentNode) document.head.appendChild(robots);
}
